import { hasFormBasedOptions, resolveOptionsWithFormValues } from '../options-resolver';

// ============================================================================
// Dynamic Options Resolution
// ============================================================================
// Static options (countries, adminDivisions, field-level exclude/include) are resolved once in
// processFields (process-static-content), and exclude/include are stripped after resolution so
// we don't reprocess. This module only resolves options that depend on form values (form-based sources).

// Resolve dynamic options for a single field
// Processing order (matches static): field options -> subFields recursively
export const resolveDynamicFieldOptions = (
  field,
  currentFormValues,
  optionsLookup,
  fieldContext,
  conditionContext = {},
) => {
  if (!field || !field.options) return field;

  if (hasFormBasedOptions(field.options)) {
    const resolvedOptions = resolveOptionsWithFormValues(
      field.options,
      currentFormValues,
      optionsLookup,
      fieldContext,
    );
    return { ...field, options: resolvedOptions };
  }

  return field;
};

// Process subFields recursively to resolve dynamic options
// Static source options (countries, adminDivisions) are already resolved in processFields
// This only handles dynamic options that need reactive resolution based on form values
// Backend extracts dynamicDependencyCodes from subFields and formats them with {i} pattern
// (e.g., "parent.{i}.child" for sibling subFields, "parent" for array itself)
// Processing order matches static: field options -> subFields recursively
export const processSubFieldOptions = (
  subFields,
  currentFormValues,
  optionsLookup,
  fieldContext,
  conditionContext = {},
) => {
  if (!Array.isArray(subFields)) return subFields;
  return subFields.map(sf => {
    const processedField = resolveDynamicFieldOptions(
      sf,
      currentFormValues,
      optionsLookup,
      fieldContext,
      conditionContext,
    );

    if (Array.isArray(processedField.subFields)) {
      processedField.subFields = processSubFieldOptions(
        processedField.subFields,
        currentFormValues,
        optionsLookup,
        fieldContext,
        conditionContext,
      );
    }

    return processedField;
  });
};

// Process a field with dynamic dependencies (options that depend on form values)
// Handles both field-level options and subFields recursively
export const processDynamicField = (
  field,
  currentFormValues,
  optionsLookup,
  fieldContext,
  conditionContext = {},
) => {
  if (!field) return field;

  const processedField = resolveDynamicFieldOptions(
    field,
    currentFormValues,
    optionsLookup,
    fieldContext,
    conditionContext,
  );

  if (Array.isArray(processedField.subFields)) {
    processedField.subFields = processSubFieldOptions(
      processedField.subFields,
      currentFormValues,
      optionsLookup,
      fieldContext,
      conditionContext,
    );
  }

  return processedField;
};
